import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const CreateCalendarDialog = ({ open, onClose, onCalendarCreated }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('personal');
  const [initialBalance, setInitialBalance] = useState('');
  const [initialDate, setInitialDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/calendars`,
        {
          name,
          type,
          initial_balance: parseFloat(initialBalance),
          initial_balance_date: initialDate,
          currency: '€'
        },
        { withCredentials: true }
      );

      onCalendarCreated(response.data);
      setName('');
      setType('personal');
      setInitialBalance('');
    } catch (error) {
      console.error('Error creating calendar:', error);
      toast.error('Erreur lors de la création du calendrier');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="create-calendar-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Nouveau calendrier</DialogTitle>
          <DialogDescription>
            Créez un calendrier personnel ou partagé pour gérer votre budget
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="calendar-name">Nom du calendrier</Label>
            <Input
              id="calendar-name"
              placeholder="Mon budget, Budget familial..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="calendar-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Type de calendrier</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="calendar-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">Personnel (uniquement vous)</SelectItem>
                <SelectItem value="joint">Joint (maximum 2 personnes)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="initial-balance">Solde initial (€)</Label>
            <Input
              id="initial-balance"
              type="number"
              step="0.01"
              placeholder="1000.00"
              value={initialBalance}
              onChange={(e) => setInitialBalance(e.target.value)}
              required
              data-testid="calendar-initial-balance-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="initial-date">Date du solde initial</Label>
            <Input
              id="initial-date"
              type="date"
              value={initialDate}
              onChange={(e) => setInitialDate(e.target.value)}
              required
              data-testid="calendar-initial-date-input"
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Annuler
            </Button>
            <Button type="submit" disabled={loading} className="flex-1" data-testid="create-calendar-submit">
              {loading ? 'Création...' : 'Créer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCalendarDialog;